import { LogoIcon } from "@/assets/icons/logo";
import { IconButton } from "../ui/icon-button";
import { TextButton } from "../ui/text-button";
import { useTranslation } from "@/hooks/use-translation";

export const Footer = () => {
   const { translation, currentLanguage } = useTranslation();
   const { footer } = translation;

   const siteBase = "https://nordroof.com";
   const lang = currentLanguage; // "en" or "is"

   // Helper for nordroof.com links
   const external = (path: string) =>
      `${siteBase}/${lang}${path.startsWith("/") ? path : `/${path}`}`;

   const companyLinks = [
      { key: "aboutUs", href: external("/about-us/") },
      { key: "projects", href: external("/modular-projects/") },
      { key: "products", href: external("/products/") },
      { key: "configurator", href: "https://configurator-homes.vercel.app/" },
      { key: "team", href: external("/contact-us/#team") },
   ] as const;

   const productLinks = [
      { key: "commercial", href: external("/commercial/") },
      { key: "private", href: external("/private/") },
   ] as const;

   const infoLinks = [
      { key: "forBusiness", href: external("/commercial/") },
      { key: "forPrivate", href: external("/private/") },
   ] as const;

   return (
      <footer className="bg-custom-grey pt-28 pb-14 px-6 sm:px-10 lg:px-10 mt-28">
         <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-16 gap-x-10">
            {/* Brand */}
            <div className="space-y-6">
               <LogoIcon />
               <p className="text-d-body-m-paragraph text-custom-dark/70">
                  {footer.brandDescription}
               </p>
               <div className="flex space-x-2">
                  <IconButton
                     aria-label={footer.social.instagram}
                     variant="social-instagram"
                  />
                  <IconButton
                     aria-label={footer.social.facebook}
                     variant="social-facebook"
                  />
                  <IconButton
                     aria-label={footer.social.linkedin}
                     variant="social-linkedin"
                  />
                  <IconButton
                     aria-label={footer.social.youtube}
                     variant="social-youtube"
                  />
               </div>
            </div>

            {/* Company */}
            <div>
               <h6 className="text-d-body-m-paragraph text-custom-dark/70 mb-4">
                  {footer.nav.company.title}
               </h6>
               <div className="space-y-2">
                  {companyLinks.map(({ key, href }) => (
                     <TextButton
                        key={key}
                        variant="link-underline"
                        className="block"
                     >
                        <a href={href}>{footer.nav.company.links[key]}</a>
                     </TextButton>
                  ))}
               </div>
            </div>

            {/* Products & Info */}
            <div className="space-y-12">
               <div>
                  <h6 className="text-d-body-m-paragraph text-custom-dark/70 mb-4">
                     {footer.nav.products.title}
                  </h6>
                  <div className="space-y-2">
                     {productLinks.map(({ key, href }) => (
                        <TextButton
                           key={key}
                           variant="link-underline"
                           className="block"
                        >
                           <a href={href}>{footer.nav.products.links[key]}</a>
                        </TextButton>
                     ))}
                  </div>
               </div>
               <div>
                  <h6 className="text-d-body-m-paragraph text-custom-dark/70 mb-4">
                     {footer.nav.info.title}
                  </h6>
                  <div className="space-y-2">
                     {infoLinks.map(({ key, href }) => (
                        <TextButton
                           key={key}
                           variant="link-underline"
                           className="block"
                        >
                           <a href={href}>{footer.nav.info.links[key]}</a>
                        </TextButton>
                     ))}
                  </div>
               </div>
            </div>

            {/* Contacts */}
            <div>
               <h6 className="text-d-body-m-paragraph text-custom-dark/70 mb-4">
                  {footer.nav.contacts.title}
               </h6>
               <div className="space-y-2">
                  <TextButton variant="link-underline" className="block">
                     <a href={`mailto:${footer.nav.contacts.email}`}>
                        {footer.nav.contacts.email}
                     </a>
                  </TextButton>
                  <TextButton variant="link-underline" className="block">
                     <a
                        href={`tel:${footer.nav.contacts.phone.replace(
                           /\s+/g,
                           ""
                        )}`}
                     >
                        {footer.nav.contacts.phone}
                     </a>
                  </TextButton>
               </div>
            </div>
         </div>

         {/* Bottom bar */}
         <div className="mt-14 border-t border-gray-200 pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center px-6 sm:px-10 lg:px-10">
            <p className="text-d-body-m-paragraph text-custom-dark/70">
               {footer.bottomBar.copyright}
            </p>
            <div className="flex space-x-8 mt-4 sm:mt-0">
               <a
                  href={external("/privacy-policy/")}
                  className="text-d-body-m-paragraph text-custom-dark/70 hover:underline"
               >
                  {footer.bottomBar.privacyPolicy}
               </a>
               <a
                  href={external("/terms-and-conditions/")}
                  className="text-d-body-m-paragraph text-custom-dark/70 hover:underline"
               >
                  {footer.bottomBar.termsAndConditions}
               </a>
            </div>
         </div>
      </footer>
   );
};
